/**
 * @typedef {Record<string, {parts:[{x: number, y: number, width: number, height: number}]}>} UVConfigs
 */
/**
 * @typedef {Record<string, {albedo_color: string}>} CustomizeMeshConfigs
 */
/**
 * @typedef {(event: String, data: any) => void} ThreeDEventsListener
 */

import {
    ActionManager,
    ArcRotateCamera,
    BoundingInfo,
    Camera,
    Color3,
    Color4,
    DirectionalLight,
    DynamicTexture,
    Engine,
    ExecuteCodeAction,
    HemisphericLight,
    HighlightLayer,
    PBRMaterial,
    PointerEventTypes,
    Scene,
    SceneLoader,
    Texture,
    Vector3,
} from '@babylonjs/core'
import '@babylonjs/loaders'
import { BabylonLoading } from './BabylonLoading'

class Babylon {
    /**
     * 
     * @param {HTMLCanvasElement} renderCanvas 
     * @param {ThreeDEventsListener} listener
     */
   constructor(renderCanvas, configs, listener) {
        this.DESIGN_BACKGROUND = '#ffffff'
        this.DEFAULT_CAMERA_RADIUS = 6
        this.TEXTURE_SIZE = configs.texture.size
        this.MATERIAL_CONFIGS = configs.material

        /** @type {UVConfigs} */
        this.uvConfigs = configs['uv_map_configs']
        /** @type {CustomizeMeshConfigs} */
        this.customizeMeshConfigs = configs['customize_mesh_configs'] || {}
        /** @type {ThreeDEventsListener} */
        this.listener = listener

        /** @type {OffscreenCanvas} */
        this.designCanvas = new OffscreenCanvas(this.TEXTURE_SIZE, this.TEXTURE_SIZE)
        this.renderCanvas = renderCanvas

        // engine
        const workCanvas = document.createElement('canvas')
        this.engine = new Engine(workCanvas, true, {
            preserveDrawingBuffer: false,
            antialias: true,
            stencil: true,
        })
        this.engine.setHardwareScalingLevel(1 / window.devicePixelRatio)
        this.engine.inputElement = this.renderCanvas
        
        this.engine.loadingScreen = new BabylonLoading()
        this.engine.displayLoadingUI()
        
        // scene
        this.scene = new Scene(this.engine)
        this.scene.useRightHandedSystem = true
        this.scene.clearColor = new Color4(0, 0, 0, 0)

        // main camera
        this.mainCamera = new ArcRotateCamera('MainCamera', Math.PI/2, Math.PI/2, configs.camera.radius, Vector3.Zero(), this.scene)
        this.mainCamera.lowerRadiusLimit = configs.camera.radius_min
        this.mainCamera.upperRadiusLimit = configs.camera.radius_max
        this.mainCamera.wheelPrecision = configs.camera.wheel_precision
        this.mainCamera.attachControl(true, false, false)
        this.mainCamera.inputs.attached.pointers.buttons = [0, 2]

        this.engine.registerView(this.renderCanvas, this.mainCamera, true)

        // highlight layer
        this.highlightLayer = new HighlightLayer('HighlightLayer', this.scene, {
            camera: this.mainCamera,
            isStroke: false,
            mainTextureRatio: 2,
        })

        // design texture
        this.designTexture = new DynamicTexture('DesignTexture', this.designCanvas, {
            scene: this.scene,
            samplingMode: Texture.NEAREST_LINEAR,
        })

        // IMPROVED LIGHTING SETUP
        // Ambient light for overall illumination
        const ambientLight = new HemisphericLight('AmbientLight', new Vector3(0, 1, 0), this.scene)
        ambientLight.intensity = configs.light?.ambient?.intensity || 0.45  // Reduced for more shadow contrast
        ambientLight.groundColor = Color3.FromArray(configs.light?.ambient?.groundColor || [0.25, 0.25, 0.25])  // Darker ground for more definition

        // Main directional light that follows camera direction
        this.mainLight = new DirectionalLight('MainLight', Vector3.Forward(), this.scene)
        this.mainLight.intensity = configs.light?.main?.intensity || 1.1  // Strong main light for clear shadows

        // Side light for emphasizing surface details and pleats
        this.sideLight = new DirectionalLight('SideLight', new Vector3(1, 0.5, 0), this.scene)
        this.sideLight.intensity = 0.6  // Side lighting to catch pleat edges
        
        // Fill light for reducing harsh shadows
        this.fillLight = new DirectionalLight('FillLight', new Vector3(-0.5, 0.5, -1), this.scene)
        this.fillLight.intensity = 0.25  // Reduced fill to maintain shadow definition
        
        // Update light directions to match camera view direction
        this.scene.registerBeforeRender(() => {
            if (this.mainCamera && this.mainLight) {
                // Get the camera's forward direction (what it's looking at)
                const forward = this.mainCamera.getForwardRay().direction
                
                // Set main light to shine in the same direction as camera view
                this.mainLight.direction = forward
                
                // Side light at an angle to catch surface details
                const right = this.mainCamera.getDirection(Vector3.Right())
                const sideDirection = forward.add(right.scale(0.8)).add(new Vector3(0, -0.3, 0)).normalize()
                this.sideLight.direction = sideDirection
                
                // Fill light with opposite offset
                const fillDirection = forward.add(right.scale(-0.5)).add(new Vector3(0, 0.3, 0)).normalize()
                this.fillLight.direction = fillDirection
            }
        })

        // Single click handler for focusing camera on mesh surface
        this.scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type === PointerEventTypes.POINTERDOUBLETAP) {
                const pickInfo = pointerInfo.pickInfo
                const meshName = pickInfo?.pickedMesh?.name
                this.listener?.('mesh_double_clicked', meshName)
            }

        })

        this.clearDesignCanvas()
    }

    run() {
        this.engine.onResizeObservable.add(() => {
            const ratio = this.engine.getRenderWidth() / this.engine.getRenderHeight()
            this.mainCamera.fovMode = ratio > 1.0 ? Camera.FOVMODE_VERTICAL_FIXED : Camera.FOVMODE_HORIZONTAL_FIXED
        })

        this.engine.runRenderLoop(() => {
            if (this.scene.activeCamera) {
                this.scene.render()
            }
        })

        const container = document.getElementById('preview_live_artwork_container')
        this.resizeObserver = new ResizeObserver(() => {
            this.engine.resize()
        })
        this.resizeObserver.observe(container)

        this.engine.hideLoadingUI()
    }

    destroy() {
        this.engine.dispose()
        this.resizeObserver.disconnect()
    }

    async loadMaterialTexture(materialUrl, configs) {
        if (!materialUrl) return
        this.bumpTexture = new Texture(materialUrl)
        this.bumpTexture.level = configs.bump_level || 3
        this.bumpTexture.uScale = configs.bump_uScale || 5
        this.bumpTexture.vScale = configs.bump_vScale || 5
    }

    async loadModel(modelUrl, materialId) {
        const result = await SceneLoader.ImportMeshAsync(undefined, modelUrl, undefined, this.scene)

        const material = this.scene.getMaterialById(materialId, false)
        if (material) {
            material.albedoTexture = this.designTexture
            if (this.bumpTexture) material.bumpTexture = this.bumpTexture

            Object.keys(this.MATERIAL_CONFIGS).forEach((key) => material[key] = this.MATERIAL_CONFIGS[key])
        }

        const secMaterial = new PBRMaterial("SecMaterial", this.scene)
        secMaterial.albedoColor = new Color3(1, 1, 1)
        secMaterial.metallic = 0
        secMaterial.roughness = 0

        const customizeMeshIds = Object.keys(this.customizeMeshConfigs)

        result.meshes.forEach((mesh) => {
            if (!mesh) return

            if (customizeMeshIds.includes(mesh.id)) {
                this.setCustomizeConfigs(mesh, mesh.id)
                return
            }

            if (mesh.material?.id !== materialId) {
                mesh.material = secMaterial
                return
            }
            
            mesh.actionManager = new ActionManager(this.scene)
            mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, (_ev) => {
                this.highlightLayer.addMesh(mesh, Color3.FromHexString('#007bff'))
            }))
            mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, (_ev) => {
                this.highlightLayer.removeMesh(mesh)
            }))
        })
    }

    setCustomizeConfigs(mesh, configKey) {
        const configs = this.customizeMeshConfigs[configKey]
        if (typeof configs !== 'object' || configs === null) return

        const material = new PBRMaterial(`${mesh.id}_material`, this.scene)
        material.albedoColor = configs.albedo_color ? Color3.FromHexString(configs.albedo_color) : new Color3(1, 1, 1)
        material.emissiveColor = new Color3(0, 0, 0)
        material.metallic = configs.metallic || 0
        material.roughness = configs.roughness || 0.5
        material.metallicF0Factor = configs.metallicF0Factor || 0.15
        material.indexOfRefraction = configs.indexOfRefraction || 1.5
        mesh.material = material
    }

    updateDesignTexture() {
        this.designTexture.update()
    }

    clearDesignCanvas(x = 0, y = 0, w = this.TEXTURE_SIZE, h = this.TEXTURE_SIZE) {
        const ctx = this.designCanvas.getContext('2d')
        ctx.fillStyle = this.DESIGN_BACKGROUND
        ctx.fillRect(x, y, w, h)
    }

    drawImage(image, x, y, w, h) {
        const ctx = this.designCanvas.getContext('2d')
        ctx.save()
        ctx.setTransform(1, 0, 0, -1, 0, this.TEXTURE_SIZE)
        this.clearDesignCanvas(x, y, w, h)
        ctx.drawImage(image, x, y, w, h)
        ctx.restore()
    }

    drawUV(image, side, index) {
        const start = Date.now()
        const uvConfig = this.uvConfigs[side]?.parts?.[index]
        this.drawImage(image, uvConfig.x, uvConfig.y, uvConfig.width, uvConfig.height)
        this.updateDesignTexture()
    }

    focusOnSide(side) {
        const meshes = this.scene.meshes.filter(m => m.name.split('_')[0] === side && !m.name.endsWith('_primitive1') )
        if (meshes.length === 0) return

        let min = meshes[0].getBoundingInfo().boundingBox.minimumWorld
        let max = meshes[0].getBoundingInfo().boundingBox.maximumWorld
        meshes.forEach((m) => {
            let meshMin = m.getBoundingInfo().boundingBox.minimumWorld
            let meshMax = m.getBoundingInfo().boundingBox.maximumWorld
            min = Vector3.Minimize(min, meshMin)
            max = Vector3.Maximize(max, meshMax)
        })

        const bbox = new BoundingInfo(min, max)
        const bboxCenter = bbox.boundingBox.centerWorld

        const direction = bboxCenter.clone()
        direction.normalize()

        this.moveCameraDirectly(this.mainCamera, direction)

        this.highlightLayer.removeAllMeshes()
        meshes.forEach((m) => this.highlightLayer.addMesh(m, Color3.FromHexString('#007bff')))
    }

    moveCameraDirectly(camera, normal) {
        const currentRadius = camera.radius
        const currentTarget = camera.target.clone()
        
        const newPosition = currentTarget.add(normal.scale(currentRadius))
        
        const deltaX = newPosition.x - currentTarget.x
        // const deltaY = newPosition.y - currentTarget.y
        const deltaZ = newPosition.z - currentTarget.z
        
        camera.beta = Math.PI / 2
        camera.alpha = Math.atan2(deltaZ, deltaX)
    }
}

let babylon = null

/**
 * @param {HTMLCanvasElement} [renderCanvas]
 * @param {} [configs]
 * @param {ThreeDEventsListener} [listener]
 * @returns {Babylon}
 */
export const getBabylonInstance = (renderCanvas, configs, listener) => {
    if (babylon) return babylon

    if (renderCanvas && configs) {
        babylon = new Babylon(renderCanvas, configs, listener)
        return babylon
    }

    return null
}

export const destroyBabylonInstance = () => {
    if (babylon) {
        babylon.destroy()
        babylon = null
    }
}